import { z } from 'zod';

import { activityFilterSchema, listQuerySchema } from './pagination';

/*
 * The rate card. reference/data-model.md §2, business-rules.md §7.
 *
 * One table serving three jobs: it classifies a job, it fills the client's
 * request dropdown, and it is the catalog the invoice builder picks from.
 */

export const PRICING_UNITS = ['flat', 'hourly'] as const;
export type PricingUnit = (typeof PRICING_UNITS)[number];

/** Display labels. Never render the raw value — CLAUDE.md §4, defect P5. */
export const PRICING_UNIT_LABELS: Record<PricingUnit, string> = {
  flat: 'Flat price',
  hourly: 'Per hour',
};

/**
 * A slug is generated from the name rather than typed.
 *
 * It is the machine value and it never reaches a screen, so asking a person to
 * invent one is asking them to make a decision that does not affect anything
 * they can see. Exported because the same rule has to hold on both sides.
 */
export function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFKD')
      // Strip the combining marks NFKD just separated out, so "Café" becomes
      // "cafe" rather than "caf-".
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64)
  );
}

/**
 * The fields, before the cross-field rule.
 *
 * Kept separate so `updateServiceTypeSchema` can add `isActive` — a schema that
 * has already had `.refine()` applied is no longer an object schema and cannot
 * be extended. The refinement is applied to both at the end instead.
 */
const serviceTypeFields = z.object({
  name: z.string().trim().min(1, 'Required').max(128, 'Must be 128 characters or fewer'),
  description: z
    .string()
    .trim()
    .max(2000)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional(),

  defaultDurationMinutes: z
    .number()
    .int('Give whole minutes')
    .min(5, 'At least 5 minutes')
    .max(24 * 60, 'A single service cannot be longer than a day'),

  pricingUnit: z.enum(PRICING_UNITS),

  /*
   * Integer cents, and nullable on purpose. "Other / Custom" is a real
   * catalog entry with no price — the admin types one per invoice. Null and 0
   * are different answers: null is "not priced", 0 is "included at no
   * charge".
   */
  defaultPriceCents: z
    .number()
    .int('Money is whole cents')
    .min(0, 'A price cannot be negative')
    .max(100_000_000, 'That is more than a million dollars — check the amount')
    .nullable()
    .optional(),

  /** Appears on the client request form. */
  isRequestable: z.boolean(),
  /** Appears in the invoice builder. Separate — a trip fee is one and not the other. */
  isBillable: z.boolean(),

  sortOrder: z.number().int().min(0).max(100_000).optional(),

  /*
   * **What this service wants from whoever does it** — business-rules.md §4's
   * auto-assignment scoring, added in 7.4.
   *
   * Optional and normally empty. A service nobody has set these on scores
   * nothing for either criterion and the ranking says so in words; it does not
   * invent a match. The whole set each time, so removing one is the same call
   * as adding one — the shape `skillIds` already has on an employee.
   */
  skillIds: z.array(z.uuid()).max(50).optional(),
  positionIds: z.array(z.uuid()).max(50).optional(),
});

/**
 * A service has to appear somewhere.
 *
 * With both flags off the row exists and nothing can ever reach it — not the
 * request form, not the invoice builder. Retiring a service is what `is_active`
 * is for, and it keeps the reason visible instead of hiding it in two booleans.
 */
const mustAppearSomewhere = {
  check: (value: { isRequestable: boolean; isBillable: boolean }): boolean =>
    value.isRequestable || value.isBillable,
  message: 'A service has to appear somewhere — on the request form, on invoices, or both',
  path: ['isRequestable'] as const,
};

export const serviceTypeInputSchema = serviceTypeFields.refine(mustAppearSomewhere.check, {
  message: mustAppearSomewhere.message,
  path: [...mustAppearSomewhere.path],
});

export type ServiceTypeInput = z.infer<typeof serviceTypeInputSchema>;

export const updateServiceTypeSchema = serviceTypeFields
  .extend({ isActive: z.boolean().optional() })
  .refine(mustAppearSomewhere.check, {
    message: mustAppearSomewhere.message,
    path: [...mustAppearSomewhere.path],
  });

export type UpdateServiceTypeInput = z.infer<typeof updateServiceTypeSchema>;

export const serviceTypeListQuerySchema = listQuerySchema.extend({
  activity: activityFilterSchema,
});

export interface ServiceTypeView {
  id: string;
  slug: string;
  /** **The display value.** Never render `slug`. */
  name: string;
  description: string | null;
  defaultDurationMinutes: number;
  pricingUnit: PricingUnit;
  /** Integer cents, or null for "not priced". */
  defaultPriceCents: number | null;
  isRequestable: boolean;
  isBillable: boolean;
  sortOrder: number;
  isActive: boolean;

  /**
   * The skills and positions this service wants — business-rules.md §4.
   *
   * Names as well as ids, because these render on the services screen and a raw
   * id is not a display value (P5). Empty is the normal state and means the
   * scoring simply has nothing to match on.
   */
  skills: { id: string; name: string }[];
  positions: { id: string; name: string }[];
}
